import { useDispatch, useSelector } from 'react-redux';
import { uniq, uniqBy, uniqWith } from 'lodash';
import { PROVINCES_AVAILABLE_FILTER_CATEGORY, TRIM_KEY_FILTER_CATEGORY, MODEL_YEAR_FILTER_CATEGORY, INVENTORY_AVAILABILITY_STATUS_FILTER_CATEGORY, } from '../reducers/vehicleListFilters';
import { PaymentMethod } from '../reducers/financialCalculators';
export const useAppDispatch = () => useDispatch(); // Export a hook that can be reused to resolve types
export const useAppSelector = useSelector;
/**
 * Works in conjunction with `rootStateMiddleware`, and extracts state embedded by the middleware in `action.meta`.
 * Can only be used inside of a redux builder's `addCase` and `addMatcher` callbacks
 */
export const getStateFromAction = (action, reducerName) => action.meta.rootState[reducerName];
/**
 * Get selected filter items (selectedFilterItems) from state
 * @param {*} models
 * @returns {Array} sorted years in descending order
 */
export const getModelYears = (vehicles) => {
    const years = vehicles.map(vehicle => parseInt(vehicle.modelYear || '', 10));
    return uniq(years)
        .filter(year => year > 0)
        .sort((a, b) => a - b)
        .reverse();
};
export const getVehiclesByModelYear = (vehicles, selectedModelYear) => {
    if (!selectedModelYear)
        return vehicles;
    const modelYears = getModelYears(vehicles);
    let vehiclesByModelYear = vehicles;
    if (selectedModelYear === 'currentYear') {
        vehiclesByModelYear = vehiclesByModelYear.filter(v => { var _a; return v.modelYear === ((_a = modelYears[0]) === null || _a === void 0 ? void 0 : _a.toString()); });
    }
    if (selectedModelYear === 'previousYear') {
        vehiclesByModelYear = vehiclesByModelYear.filter(v => parseInt(v.modelYear, 10) < modelYears[0]);
    }
    // It might be a number, e.g. "2024"
    if (parseInt(selectedModelYear, 10) > 0) {
        vehiclesByModelYear = vehicles.filter(v => v.modelYear === selectedModelYear);
    }
    return vehiclesByModelYear;
};
export const getFilteredVehicles = (vehicles, selectedModelYear, selectedFilterItems) => {
    var _a;
    const selectedFilterItemsLength = selectedFilterItems === null || selectedFilterItems === void 0 ? void 0 : selectedFilterItems.length;
    const vehiclesByModelYear = getVehiclesByModelYear(vehicles, selectedModelYear);
    const filteredVehicles = (_a = vehiclesByModelYear === null || vehiclesByModelYear === void 0 ? void 0 : vehiclesByModelYear.filter) === null || _a === void 0 ? void 0 : _a.call(vehiclesByModelYear, vehicle => {
        if (selectedFilterItemsLength > 0) {
            const check = selectedFilterItems.every(item => {
                var _a;
                const [, name] = ((_a = item === null || item === void 0 ? void 0 : item.split) === null || _a === void 0 ? void 0 : _a.call(item, '|')) || [];
                const vehicleFilterValues = (vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) || [];
                return Object.values(vehicleFilterValues).some(values => name && values.includes(name));
            });
            if (!check) {
                return false;
            }
        }
        return true;
    });
    return uniqBy(filteredVehicles, 'trimKey');
};
export const getIntColorFromColorCombination = (vehicle) => { var _a, _b, _c, _d; 
// vehicle only has one transmission, extColor and intColor
return (_d = (_c = (_b = (_a = vehicle.transmissions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.exteriorColors) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.interiorColors[0]; };
// check for trimKey
const checkTrimFilters = (selectedFilterItems, vehicle) => {
    const filters = selectedFilterItems.filter(item => item.includes(TRIM_KEY_FILTER_CATEGORY));
    return (filters.length === 0 ||
        filters.some(item => {
            var _a;
            const [, trimKeysStr] = ((_a = item === null || item === void 0 ? void 0 : item.split) === null || _a === void 0 ? void 0 : _a.call(item, '|')) || [];
            const trimKeys = trimKeysStr === null || trimKeysStr === void 0 ? void 0 : trimKeysStr.split(',');
            return !trimKeys || trimKeys.includes(vehicle.trimKey);
        }));
};
// check for modelYear
const checkYearFilters = (selectedFilterItems, vehicle) => {
    const filters = selectedFilterItems.filter(item => item.includes(MODEL_YEAR_FILTER_CATEGORY));
    return (filters.length === 0 ||
        filters.some(item => {
            var _a;
            const [, modelYear] = ((_a = item === null || item === void 0 ? void 0 : item.split) === null || _a === void 0 ? void 0 : _a.call(item, '|')) || [];
            return (vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelYear) === modelYear;
        }));
};
const createVehiclesFilterFunction = (filterByItems) => (vehicles, selectedFilterItems) => { var _a; return (_a = vehicles === null || vehicles === void 0 ? void 0 : vehicles.filter) === null || _a === void 0 ? void 0 : _a.call(vehicles, vehicle => filterByItems.every(({ checkFn }) => checkFn(selectedFilterItems, vehicle))); };
export const getFilteredVehiclesByTrimAndModelYear = createVehiclesFilterFunction([
    {
        checkFn: checkTrimFilters,
    },
    {
        checkFn: checkYearFilters,
    },
]);
export const getFilteredVehiclesByModelYear = createVehiclesFilterFunction([
    {
        checkFn: checkYearFilters,
    },
]);
export const getFilteredVehiclesByTrim = createVehiclesFilterFunction([
    {
        checkFn: checkTrimFilters,
    },
]);
/**
 * Transforms an array of pipe-delimited filter item strings (e.g. ['bodyType|cars', 'bodyType|suv'])
 * into an object with categories as keys and an array of keys as values (e.g. { bodyType: ['cars', 'suv'] }).
 *
 * @param filterItems - The array of filter items to transform.
 * @returns The transformed object with categories as keys and an array of keys as values.
 */
const transformFilterItems = (filterItems) => {
    const filtersObj = filterItems === null || filterItems === void 0 ? void 0 : filterItems.reduce((acc, item) => {
        const [category, key] = item.split('|');
        // @ts-ignore
        acc[category] = [...(acc[category] || []), key];
        return acc;
    }, {});
    return filtersObj;
};
export const getEnabledFilterItems = (selectedFilterItems, vehicles, provinceCode) => {
    var _a, _b, _c;
    const selectedFilters = transformFilterItems(selectedFilterItems);
    const vehiclesArray = Object.values(vehicles);
    const enabledFilterItems = (_c = (_b = (_a = vehiclesArray === null || vehiclesArray === void 0 ? void 0 : vehiclesArray.filter) === null || _a === void 0 ? void 0 : _a.call(vehiclesArray, (vehicle) => {
        if (!(vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters))
            return false;
        return Object.keys(selectedFilters).every(filter => {
            var _a;
            return (_a = selectedFilters[filter]) === null || _a === void 0 ? void 0 : _a.every((spec) => Object.values(vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters).some(v => v.includes(spec)));
        });
    })) === null || _b === void 0 ? void 0 : _b.reduce) === null || _c === void 0 ? void 0 : _c.call(_b, (acc, vehicle) => {
        var _a, _b, _c, _d, _e;
        if (vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) {
            // Filter is evaluated only if provinces array is empty or includes the user province
            const provincesLength = (_c = (_b = (_a = vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) === null || _a === void 0 ? void 0 : _a[PROVINCES_AVAILABLE_FILTER_CATEGORY]) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
            if (provincesLength === 0 || ((_e = (_d = vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) === null || _d === void 0 ? void 0 : _d[PROVINCES_AVAILABLE_FILTER_CATEGORY]) === null || _e === void 0 ? void 0 : _e.includes(provinceCode))) {
                Object.keys(vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters).forEach(cat => {
                    var _a, _b, _c;
                    if (cat === PROVINCES_AVAILABLE_FILTER_CATEGORY)
                        return;
                    (_c = (_b = (_a = vehicle === null || vehicle === void 0 ? void 0 : vehicle.filters) === null || _a === void 0 ? void 0 : _a[cat]) === null || _b === void 0 ? void 0 : _b.forEach) === null || _c === void 0 ? void 0 : _c.call(_b, key => {
                        acc.push(`${cat}|${key}`);
                    });
                });
            }
        }
        return acc;
    }, []);
    return [...new Set(enabledFilterItems)];
};
export const getEnabledInventoryAvailabilityStatusFilterItems = (vehicles) => {
    const enabledFilterItems = [];
    vehicles.forEach(vehicle => {
        var _a, _b, _c;
        const interiorColor = ((_a = vehicle.transmissions[0]) === null || _a === void 0 ? void 0 : _a.exteriorColors)
            ? (_c = (_b = vehicle.transmissions[0]) === null || _b === void 0 ? void 0 : _b.exteriorColors[0]) === null || _c === void 0 ? void 0 : _c.interiorColors[0]
            : null;
        if (interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.inStock) {
            enabledFilterItems.push(`${INVENTORY_AVAILABILITY_STATUS_FILTER_CATEGORY}|inStock`);
        }
        if (interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.onItsWay) {
            enabledFilterItems.push(`${INVENTORY_AVAILABILITY_STATUS_FILTER_CATEGORY}|onItsWay`);
        }
    });
    return [...new Set(enabledFilterItems)];
};
// This utility returns one vehicle per transmission/exteriorColor/interiorColor combination
export const transformVehiclesByTransmissionAndColor = (vehicles) => {
    const colorCombinations = vehicles.reduce((acc, vehicle) => {
        const transmissions = vehicle.transmissions || [];
        transmissions.forEach(transmission => {
            const exteriorColors = transmission.exteriorColors || [];
            exteriorColors === null || exteriorColors === void 0 ? void 0 : exteriorColors.forEach(exteriorColor => {
                const interiorColors = exteriorColor.interiorColors || [];
                interiorColors.forEach(interiorColor => {
                    var _a, _b;
                    const value = {
                        compareTrimKeyFeatures: vehicle.compareTrimKeyFeatures,
                        defaultTransmission: transmission.transmissionKey, // we only have one transmission, so we make it the default
                        detIdentifier: vehicle.detIdentifier,
                        filters: vehicle.filters,
                        modelName: vehicle.modelName,
                        modelKey: vehicle.modelKey,
                        modelYear: vehicle.modelYear,
                        trimFeaturesPageUrl: vehicle.trimFeaturesPageUrl,
                        trimImage: {
                            primaryThumbnail: (_a = exteriorColor.trimImage) === null || _a === void 0 ? void 0 : _a.primaryImage,
                            secondaryThumbnail: (_b = exteriorColor.trimImage) === null || _b === void 0 ? void 0 : _b.secondaryImage,
                        },
                        trimKey: vehicle.trimKey,
                        trimName: vehicle.trimName,
                        trimGtmName: vehicle.trimGtmName,
                        videoUrl: vehicle.videoUrl,
                        transmissions: [
                            {
                                ...transmission,
                                exteriorColors: [
                                    {
                                        ...exteriorColor,
                                        interiorColors: [interiorColor],
                                    },
                                ],
                                exteriorColorKey: exteriorColor.key,
                                interiorColorKey: interiorColor.key,
                            },
                        ],
                    };
                    acc.push(value);
                });
            });
        });
        return acc;
    }, []);
    return uniqWith(colorCombinations, (a, b) => {
        const vehicleA = a === null || a === void 0 ? void 0 : a.transmissions[0];
        const vehicleAExteriorColor = vehicleA === null || vehicleA === void 0 ? void 0 : vehicleA.exteriorColorKey;
        const vehicleAInteriorColor = vehicleA === null || vehicleA === void 0 ? void 0 : vehicleA.interiorColorKey;
        const vehicleB = b === null || b === void 0 ? void 0 : b.transmissions[0];
        const vehicleBExteriorColor = vehicleB === null || vehicleB === void 0 ? void 0 : vehicleB.exteriorColorKey;
        const vehicleBInteriorColor = vehicleB === null || vehicleB === void 0 ? void 0 : vehicleB.interiorColorKey;
        return ((vehicleA === null || vehicleA === void 0 ? void 0 : vehicleA.transmissionKey) === (vehicleB === null || vehicleB === void 0 ? void 0 : vehicleB.transmissionKey) &&
            vehicleAExteriorColor === vehicleBExteriorColor &&
            vehicleAInteriorColor === vehicleBInteriorColor);
    });
};
export const sortPaymentMethods = (paymentMethods) => {
    const defaultOrder = [PaymentMethod.lease, PaymentMethod.finance, PaymentMethod.cash];
    if (!paymentMethods || (paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.length) === 0) {
        return defaultOrder;
    }
    return paymentMethods.sort((a, b) => defaultOrder.indexOf(a) - defaultOrder.indexOf(b));
};
export const sanitizeUrl = (urlAddress) => {
    var _a, _b, _c, _d, _e, _f;
    if (!urlAddress) {
        return undefined;
    }
    const urlRegex = 
    // eslint-disable-next-line max-len
    /^(?:(?:https?|ftp):\/\/)?(?:\S+(?::\S*)?@)?(?:www\.)?([a-zA-Z0-9_-]+\.)*[a-zA-Z0-9_-]+\.[a-zA-Z]{2,}(?::\d{1,5})?(?:[/?#]\S*)?$/;
    const validUrl = urlRegex.test(urlAddress);
    if (!validUrl)
        return undefined;
    let domain = urlAddress.replace(/^(https?|ftp):\/\//, '');
    domain = (_f = (_e = (_d = (_c = (_b = (_a = domain === null || domain === void 0 ? void 0 : domain.split('/')) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.split('?')) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.split('#')) === null || _e === void 0 ? void 0 : _e[0]) !== null && _f !== void 0 ? _f : '';
    if (!domain.includes('@')) {
        // if its not an email
        const parts = domain.split('.');
        if (parts.length > 2) {
            domain = parts.slice(1).join('.');
        }
    }
    return domain;
};
